import { useCallback } from "react";

import { filterObjectFields, filterObjectFieldsForArray } from "../../utils/objectUtil";
import { SaveChangeResult } from "../../components/dataGridActionUtil";

import { OrganizationChildCrudDataGridProps } from "./model";
import { CrudDataGrid } from "./CrudDataGrid";
import { auditFieldNames } from "../../model/metaData";

export const organizationDataToBeFiltered = [
  "organizationId",
  "organization",
  "createdByUser",
  "lastUpdatedByUser",
];


export const OrganizationChildCrudDataGrid = (props: OrganizationChildCrudDataGridProps) => {
  props.logger?.debug("Rendering OrganizationChildCrudDataGrid");

  const allDataToBeFiltered = [...(props.dataToBeFiltered || []), ...organizationDataToBeFiltered];
  const defaultFilterRowDataForForm = (data: any) => filterObjectFields(data, 
    auditFieldNames.concat(props.dataToBeFilteredForForm || []));
  const filterRowDataForForm = props.filterRowDataForForm ?? defaultFilterRowDataForForm;

  const refreshData = useCallback(async () => {
    const response = await props.pageProps.appUtil.httpClients["eAccountingService"].post<any[], any>(
      `/${props.baseUrl}/get-all-by-parent-id`,
      { parentId: props.parentKey },
      (err: any) => {
        throw err;
      },
    );
    if (response) {
      return filterObjectFieldsForArray(response as any[], allDataToBeFiltered);
    }
    return [];
  }, [props.baseUrl, props.parentKey, props.pageProps.appUtil.httpClients, allDataToBeFiltered]);

  const sendChange = useCallback(async (change: {
    type: any;
    data: any;
    key: any;
  }): Promise<SaveChangeResult> => {
    const keyExpr = Array.isArray(props.keyExpr) ? props.keyExpr[0] : props.keyExpr;
    switch (change.type) {
      case "insert":
        const response = await props.pageProps.appUtil.httpClients["eAccountingService"].post(
          `/${props.baseUrl}/create`,
          { data: { ...props.parentKey, ...change.data } },
          (err: any) => {
            throw err;
          },
        );
        if (response && (response as any).createdTime) {
          return {
            data: filterObjectFields(response, allDataToBeFiltered),
            keyExpr: keyExpr,
          };
        }
        break;
      case "update":
        const updateUrl = `/${props.baseUrl}/update-fields`;
        const updateData = {
          key: change.key,
          organizationId: props.parentKey.organizationId,
          updateValues: change.data,
        };
        const updateResponse = await props.pageProps.appUtil.httpClients["eAccountingService"].put(
          updateUrl,
          updateData,
          (err: any) => {
            throw err;
          },
        );
        if (updateResponse && (updateResponse as any).lastUpdatedTime) {
          return {
            data: filterObjectFields(updateResponse, allDataToBeFiltered),
            keyExpr: keyExpr,
          };
        }
        break;
      case "remove":
        const deleteUrl = `/${props.baseUrl}/delete-by-id`;
        const deleteResponse = await props.pageProps.appUtil.httpClients["eAccountingService"].put(
          deleteUrl,
          { organizationId: props.parentKey.organizationId, key: change.key },
          (err: any) => {
            throw err;
          },
        );
        if (deleteResponse && (deleteResponse as any).result) {
          return {
            data: "",
            keyExpr: keyExpr,
          };
        }
        break;
      default:
        break;
    }
    return {
      data: change.data,
      keyExpr: keyExpr,
    };
  }, [props.baseUrl, props.parentKey, props.keyExpr, props.pageProps.appUtil.httpClients, allDataToBeFiltered]);

  return (
    <CrudDataGrid
      pageProps={props.pageProps}
      logger={props.logger}
      dummyRow={props.dummyRow}
      keyExpr={props.keyExpr}
      isKeyAutoGenerated={props.isKeyAutoGenerated}
      formTitle={props.formTitle}
      formColCount={props.formColCount}
      formColSpan={props.formColSpan}
      refreshData={refreshData}
      allowAdding={props.allowAdding}
      allowUpdating={props.allowUpdating}
      allowDeleting={props.allowDeleting}
      filterRowDataForForm={filterRowDataForForm}
      sendChange={sendChange}
      hideColumnChooser={props.hideColumnChooser}
      hideSearchPanel={props.hideSearchPanel}
      showFilterRow={props.showFilterRow}
      hideInactiveButton={props.hideInactiveButton}
      columnConfigs={props.columnConfigs}
      excludeKeyColumns={props.excludeKeyColumns}
      excludedColumns={props.excludedColumns}
      editorItemOptions={props.editorItemOptions}
      masterDetailComponent={props.masterDetailComponent}
      additionalButtons={props.additionalButtons}
      selectionMode={props.selectionMode}
      onSelectionChanged={props.onSelectionChanged}
    />
  );
};
